import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authApi, setAccessToken } from '../lib/api';
import { connectSocket } from '../lib/socket';
import { useAuthStore } from '../stores/auth';
import { apiErrorMessage } from '../lib/apiError';

export default function SetupPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();

  const [password, setPassword]     = useState('');
  const [confirm, setConfirm]       = useState('');
  const [showPw, setShowPw]         = useState(false);
  const [error, setError]           = useState('');
  const [loading, setLoading]       = useState(false);
  const [success, setSuccess]       = useState(false);

  useEffect(() => {
    if (!token) setError('Invalid setup link. Ask your admin to resend the invite.');
  }, [token]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }

    setLoading(true);
    try {
      const { data } = await authApi.setupPassword(token, password);
      setAccessToken(data.accessToken);
      connectSocket(data.accessToken);
      useAuthStore.setState({ user: data.user, accessToken: data.accessToken });
      setSuccess(true);
      setTimeout(() => navigate('/'), 1500);
    } catch (err: unknown) {
      setError(apiErrorMessage(err, 'Setup failed. The link may have expired — ask your admin to resend.'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--vbg)',
    }}>
      <div style={{
        width: 380, background: 'var(--vs1)', border: '1px solid var(--vb)',
        borderRadius: 6, padding: '40px 36px',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 8 }}>
            <img src="/logo.svg" style={{ height: 40, width: 'auto' }} />
            <span style={{ fontSize: '2rem', fontWeight: 800, letterSpacing: '-0.02em' }}>
              <span style={{ color: 'var(--shell-text)' }}>Relay</span><span style={{ color: 'var(--brand-accent)' }}>Through</span>
            </span>
          </div>
          <div style={{ color: 'var(--shell-muted)', fontSize: '0.82rem' }}>
            Set Up Your Account
          </div>
        </div>

        {success ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: 12 }}>✓</div>
            <div style={{ color: 'var(--v2)', fontWeight: 700, marginBottom: 6 }}>Account ready!</div>
            <div style={{ color: 'var(--shell-xs)', fontSize: '0.82rem' }}>Taking you to the app…</div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontSize: '0.82rem', color: 'var(--shell-xs)', lineHeight: 1.5 }}>
              Choose a password to activate your account.
            </div>

            <div>
              <label style={labelStyle}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                  placeholder="Min. 8 characters"
                  style={{ ...inputStyle, paddingRight: 36 }}
                />
                <button type="button" onClick={() => setShowPw((v) => !v)} style={eyeBtn}>
                  {showPw ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <div>
              <label style={labelStyle}>Confirm Password</label>
              <input
                type={showPw ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                placeholder="Repeat password"
                style={inputStyle}
              />
            </div>

            {error && (
              <div style={{
                background: 'rgba(239,68,68,.12)', border: '1px solid rgba(239,68,68,.3)',
                borderRadius: 3, padding: '8px 12px', color: 'var(--red)', fontSize: '0.8rem',
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !token}
              style={{
                background: (loading || !token) ? 'var(--v3)' : 'var(--v1)', color: 'var(--text-on-accent)',
                border: 'none', borderRadius: 3, padding: '10px 0',
                fontWeight: 700, fontSize: '0.88rem',
                cursor: (loading || !token) ? 'not-allowed' : 'pointer',
                marginTop: 4, transition: 'background .15s',
              }}
            >
              {loading ? 'Setting up…' : 'Activate Account'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '0.75rem', color: 'var(--shell-xs)',
  marginBottom: 6, fontWeight: 600,
};
const inputStyle: React.CSSProperties = {
  width: '100%', background: 'var(--vs2)', border: '1px solid var(--vb)',
  borderRadius: 3, padding: '8px 12px', color: 'var(--text)', fontSize: '0.88rem',
  boxSizing: 'border-box',
};
const eyeBtn: React.CSSProperties = {
  position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--shell-xs)', fontSize: '0.9rem', padding: 0, lineHeight: 1,
};
